import React from 'react';
import { Box, Button, Combobox, Container, Field, Heading, HStack, Image, Input, Skeleton, SkeletonText, Stack, Text, Toast, Toaster, createToaster, useListCollection, VStack } from '@chakra-ui/react';
import { Navigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { API_ENDPOINTS, apiClient, DONATE_PLACEMENTS, getDonationFormBySlug, getDefaultDonationForm, isObject, logger, parseCurrencyInputToNumber, type CampaignEntity, type DonationForm as DonationFormModel, type DonationFrequency } from '@kuow/core';
import { DonationForm, useAuth, useDonateImpression } from '@kuow/shared-ui';

import niceCheckmark from '../assets/nice-checkmark.svg';
import { getAssetUrl, buildImgixUrl, getImpactContent } from '../utils/donationFormTheme';
import { InitialDataContext } from '../context/InitialDataContext';

const DONATE_FALLBACK_PATH = '/';

const isDonationFormUnavailable = (form: DonationFormModel, options?: { allowDraft?: boolean }): boolean => {
  if (form.archived) return true;
  if (form.status === 'draft' && !options?.allowDraft) return true;
  return false;
};

const parseTierParamAmount = (raw: string | null): number | null => {
  if (!raw) return null;
  const parsed = parseCurrencyInputToNumber(raw);
  if (!parsed || parsed <= 0) return null;
  return parsed;
};

const formatTierAmount = (amount: number): string => {
  const rounded = Math.round(amount * 100) / 100;
  return String(rounded);
};

const toaster = createToaster({ placement: 'top' });

// NOTE: This is a UI-only speed bump (not a security boundary).
// The value is discoverable in the client bundle, so it must not be treated as a secret.
// Follow-up: validate call center access server-side via a dedicated endpoint.
const CALL_CENTER_ACCESS_CODE_SPEED_BUMP = 'kuow949';

const buildTierOverrides = (firstTierAmount: number) => {
  const multipliers = [1, 1.5, 2, 2.5];
  return {
    donation_tiers: multipliers.map((multiplier, index) => {
      const single = firstTierAmount * multiplier;
      const monthly = single / 12;
      return {
        tier: index + 1,
        single_donation_amount: formatTierAmount(single),
        monthly_donation_amount: formatTierAmount(monthly),
        flag: null,
      };
    }),
  };
};

export const DonatePage: React.FC = () => {
  const { setHideHeaderFooter } = useOutletContext<{ setHideHeaderFooter: (hide: boolean) => void }>();
  const { formSlug } = useParams();
  const [searchParams] = useSearchParams();
  const utmCampaign = searchParams.get('utm_campaign');
  const tierParam = searchParams.get('tier');
  const sfmcSubkeyParam = searchParams.get('sfmc_subkey');
  const previewParam = searchParams.get('preview');
  const normalizedSfmcSubkey = sfmcSubkeyParam?.trim() ?? null;
  const isPreview = previewParam?.trim().toLowerCase() === 'true';

  const amountParam = searchParams.get('amount');
  const frequencyParam = searchParams.get('frequency');

  const { member } = useAuth();
  const prefillMemberId = member?.id ?? undefined;
  const prefillDonor = member ?? null;

  const prefillSelection = React.useMemo(() => {
    const hasFrequency = frequencyParam === 'monthly' || frequencyParam === 'one-time';
    const amount = amountParam ? parseCurrencyInputToNumber(amountParam) : null;
    const validAmount = amount !== null && amount > 0 ? amount : null;

    if (!hasFrequency && validAmount === null) return undefined;

    const frequency: DonationFrequency = frequencyParam === 'monthly' ? 'monthly' : 'one-time';
    if (validAmount !== null) {
      return { frequency, kind: 'custom' as const, amount: validAmount };
    }
    return { frequency, kind: 'none' as const };
  }, [amountParam, frequencyParam]);

  const ssrData = React.useContext(InitialDataContext);
  // Track whether the SSR-provided data has been consumed so we don't skip
  // re-fetches on subsequent client-side navigations between form slugs.
  const ssrDataConsumed = React.useRef(false);

  const [isLoadingDetails, setIsLoadingDetails] = React.useState(!ssrData);
  const [shouldRedirect, setShouldRedirect] = React.useState(false);
  const [formDetails, setFormDetails] = React.useState<DonationFormModel | null>(ssrData);
  const [campaignQuery, setCampaignQuery] = React.useState('');
  interface CampaignOption { value: string; label: string };
  const [campaignItems, setCampaignItems] = React.useState<CampaignOption[]>([]);
  const [isCampaignLoading, setIsCampaignLoading] = React.useState(false);
  const [selectedCampaignCode, setSelectedCampaignCode] = React.useState(() => (utmCampaign?.trim() ? utmCampaign.trim() : ''));
  const [formResetKey, setFormResetKey] = React.useState(0);
  const [hasCallCenterAccess, setHasCallCenterAccess] = React.useState(false);
  const [passwordInput, setPasswordInput] = React.useState('');
  const [passwordError, setPasswordError] = React.useState<string | null>(null);

  const campaignCollection = useListCollection<CampaignOption>({
    initialItems: [],
    itemToString: (item) => item.label,
    itemToValue: (item) => item.value,
    limit: 25,
  });

  React.useEffect(() => {
    // On the initial mount the SSR data is already in state — skip the fetch
    // and mark the SSR data as consumed so subsequent navigations fetch fresh.
    if (!ssrDataConsumed.current && ssrData) {
      ssrDataConsumed.current = true;
      setIsLoadingDetails(false);
      return;
    }
    ssrDataConsumed.current = true;

    let isMounted = true;
    setIsLoadingDetails(true);
    setShouldRedirect(false);
    setFormDetails(null);

    const load = async () => {
      try {
        const details = formSlug
          ? await getDonationFormBySlug(formSlug, { preview: isPreview })
          : await getDefaultDonationForm({ preview: isPreview });
        if (!isMounted) return;
        if (isDonationFormUnavailable(details, { allowDraft: isPreview })) {
          logger.warn('Donation form unavailable; redirecting to fallback', { formSlug, archived: details.archived, status: details.status });
          setShouldRedirect(true);
          return;
        }
        setFormDetails(details);
      } catch (err) {
        logger.error('Failed to load donation form details for DonatePage; redirecting to fallback', { formSlug, error: err });
        if (!isMounted) return;
        setShouldRedirect(true);
      } finally {
        if (isMounted) {
          setIsLoadingDetails(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [formSlug, isPreview, ssrData]);

  React.useEffect(() => {
    if (formDetails?.kind === 'call_center') {
      setHasCallCenterAccess(false);
      setPasswordInput('');
      setPasswordError(null);
      return;
    }
    setHasCallCenterAccess(true);
    setPasswordInput('');
    setPasswordError(null);
  }, [formDetails?.kind]);

  React.useEffect(() => {
    const hide = formDetails?.kind === 'call_center';
    setHideHeaderFooter(hide);
    return () => {
      setHideHeaderFooter(false);
    };
  }, [formDetails?.kind, setHideHeaderFooter]);

  React.useEffect(() => {
    if (utmCampaign?.trim() && selectedCampaignCode.trim().length === 0) {
      setSelectedCampaignCode(utmCampaign.trim());
    }
  }, [selectedCampaignCode, utmCampaign]);

  React.useEffect(() => {
    campaignCollection.set(campaignItems);
  }, [campaignCollection, campaignItems]);

  React.useEffect(() => {
    const q = campaignQuery.trim();
    if (q.length < 2) {
      setIsCampaignLoading(false);
      setCampaignItems((prev) => {
        if (!selectedCampaignCode) return [];
        const selected = prev.find((item) => item.value === selectedCampaignCode);
        if (!selected) return [{ value: selectedCampaignCode, label: selectedCampaignCode }];
        return prev.length === 1 && prev[0]?.value === selectedCampaignCode ? prev : [selected];
      });
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      const run = async () => {
        setIsCampaignLoading(true);
        try {
          const endpoint = `${API_ENDPOINTS.CAMPAIGNS}/autocomplete?query=${encodeURIComponent(q)}&limit=25`;
          const results = await apiClient.get<CampaignEntity[]>(endpoint, { withAuth: false, signal: controller.signal });
          const next = results
            .map((item) => (isObject(item) && typeof item.code === 'string' ? { value: item.code, label: item.code } : null))
            .filter((item): item is { value: string; label: string } => Boolean(item));
          setCampaignItems(next);
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') return;
          logger.error('Failed to load campaign autocomplete', { query: q, error });
          setCampaignItems([]);
        } finally {
          setIsCampaignLoading(false);
        }
      };

      void run();
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [campaignQuery, selectedCampaignCode]);

  React.useEffect(() => {
    if (!selectedCampaignCode) return;
    setCampaignItems((prev) =>
      prev.some((item) => item.value === selectedCampaignCode)
        ? prev
        : [{ value: selectedCampaignCode, label: selectedCampaignCode }, ...prev],
    );
  }, [selectedCampaignCode]);

  const backgroundImageUrl = getAssetUrl(formDetails?.background_image);
  const themeImageUrl = getAssetUrl(formDetails?.theme_image);

  const seoTitle = formDetails?.headline ? `${formDetails.headline} | KUOW` : 'Donate | KUOW';
  const seoDescription = formDetails?.description ?? null;
  const seoImageUrl = themeImageUrl
    ? buildImgixUrl(themeImageUrl, { w: 1200, h: 630, fit: 'crop' })
    : null;

  const { impactHeadline, impactBullets, hasImpactSection } = getImpactContent(formDetails);
  const isCallCenterForm = formDetails?.kind === 'call_center';
  const placement = isCallCenterForm ? DONATE_PLACEMENTS.callCenter : DONATE_PLACEMENTS.userPortal;
  const formColumnRef = React.useRef<HTMLDivElement>(null);
  // Wait for the form details: until `kind` is known, a call-center page looks like user-portal.
  useDonateImpression(formColumnRef, placement, Boolean(formDetails) && !isCallCenterForm);
  const allowCampaignEntry = Boolean(isCallCenterForm && formDetails?.allow_campaign_entry);

  const campaignDisplayValue = React.useMemo(() => {
    if (campaignQuery.trim().length > 0) return campaignQuery;
    if (selectedCampaignCode.trim().length > 0) return selectedCampaignCode;
    return '';
  }, [campaignQuery, selectedCampaignCode]);

  const isCampaignPlaceholder = React.useMemo(() => {
    return campaignQuery.trim().length === 0 && selectedCampaignCode.trim().length === 0;
  }, [campaignQuery, selectedCampaignCode]);

  const utmCampaignOverride = isCallCenterForm
    ? (allowCampaignEntry ? selectedCampaignCode.trim() || null : utmCampaign?.trim() ?? null)
    : utmCampaign?.trim() ?? null;

  const tierOverrides = React.useMemo(() => {
    const amount = parseTierParamAmount(tierParam?.trim() ?? null);
    if (!amount) return undefined;
    return buildTierOverrides(amount);
  }, [tierParam]);

  const requiresPasswordGate = Boolean(isCallCenterForm && !hasCallCenterAccess);
  const submitCallCenterPassword = React.useCallback(() => {
    const trimmed = passwordInput.trim();
    if (trimmed === CALL_CENTER_ACCESS_CODE_SPEED_BUMP) {
      setHasCallCenterAccess(true);
      setPasswordError(null);
      return;
    }
    setPasswordError('Incorrect password');
  }, [passwordInput]);

  const handleStartNewDonation = React.useCallback(() => {
    const confirmed = window.confirm('Would you like to continue and lose all progress on the current donation?');
    if (!confirmed) return;
    setSelectedCampaignCode('');
    setCampaignQuery('');
    setCampaignItems([]);
    setFormResetKey((prev) => prev + 1);
    toaster.create({
      title: 'New Donation Started!',
      type: 'success',
    });
  }, []);

  if (shouldRedirect && formSlug) {
    return <Navigate to={DONATE_FALLBACK_PATH} replace />;
  }

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        {seoDescription && <meta name="description" content={seoDescription} />}
        <meta property="og:title" content={seoTitle} />
        {seoDescription && <meta property="og:description" content={seoDescription} />}
        {seoImageUrl && <meta property="og:image" content={seoImageUrl} />}
        {seoImageUrl && <meta property="og:image:width" content="1200" />}
        {seoImageUrl && <meta property="og:image:height" content="630" />}
        <meta property="og:type" content="website" />
      </Helmet>
    <Box
      w="full"
      flex="1"
      display="flex"
      flexDirection="column"
      position="relative"
      backgroundImage={backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined}
      backgroundRepeat="no-repeat"
      backgroundPosition="center"
      backgroundSize="cover"
      _before={
        backgroundImageUrl
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              bg: 'blackAlpha.600',
            }
          : undefined
      }
    >
      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        justifyContent={{ base: 'flex-start', md: 'center' }}
        py={{ base: 10, md: 10 }}
      >
        <Container maxW="7xl" px={{ base: 6, md: 12 }} position="relative" zIndex={1}>
          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            align={{ base: 'center', md: 'flex-start' }}
            justify="center"
            gap={{ base: 10, md: 12 }}
          >
            {!isCallCenterForm ? (
              <Box
                flex="1"
                color="fg.inverted"
                minW="0"
                maxW="lg"
              >
                <VStack align="start" gap={{ base: 6, lg: 8 }} pt={{ base: 0, md: 12 }} pb={{ base: 10, md: 12 }}>
                  <>
                    {isLoadingDetails ? (
                      <Box w="full">
                        <Skeleton h={11} borderRadius="md" />
                      </Box>
                    ) : themeImageUrl ? (
                      <Stack w="full" gap={{ base: 6, lg: 8 }} direction={{ base: 'column', lg: 'row' }} align={{ base: 'center', lg: 'end' }}>
                        <Box
                          w="full"
                          maxW={{ base: "56", lg: "64" }}
                          aspectRatio="1"
                          flexShrink={0}
                        >
                          <Image
                            src={themeImageUrl}
                            alt={formDetails?.theme_image?.title ?? ''}
                            w="full"
                            h="full"
                            objectFit="cover"
                          />
                        </Box>
                        <Text as="h1" fontSize="4xl" fontWeight="semibold">
                          {formDetails?.headline ?? ''}
                        </Text>
                      </Stack>
                    ) : (
                      <Text as="h1" fontSize="4xl" fontWeight="semibold">
                        {formDetails?.headline ?? ''}
                      </Text>
                    )}

                    {isLoadingDetails ? (
                      <SkeletonText noOfLines={4} gap={3} w="full" />
                    ) : (
                      <Text fontSize="lg" opacity={0.9}>
                        {formDetails?.description ?? ''}
                      </Text>
                    )}

                    {!isLoadingDetails && hasImpactSection && (
                      <VStack
                        gap={2.5}
                        w="full"
                        align="stretch"
                        pt={2}
                      >
                        {impactHeadline && (
                          <Text fontWeight="semibold" fontSize="md" textAlign="center">
                            {impactHeadline}
                          </Text>
                        )}

                        {impactBullets.map((bullet, idx) => (
                          <HStack key={`${idx}:${bullet}`} gap={2.5} align="center">
                            <Image src={String(niceCheckmark)} alt="" boxSize={6.5} />
                            <Text fontSize="sm" opacity={0.9} dangerouslySetInnerHTML={{ __html: bullet }}></Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </>
                </VStack>
              </Box>
            ) : null}

            <Box
              ref={formColumnRef}
              flex="1"
              w={{ base: 'full', md: 'auto' }}
              minW={75}
              maxW="lg"
              bg={isCallCenterForm ? 'transparent' : 'bg'}
              borderRadius={isCallCenterForm ? undefined : 'l3'}
              boxShadow={isCallCenterForm ? undefined : { base: 'none', md: 'lg' }}
              py={isCallCenterForm ? undefined : { base: 4, lg: 4 }}
              pb={isCallCenterForm ? undefined : { base: 4, lg: 7 }}
            >
              {isCallCenterForm ? (
                <VStack align="stretch" gap={{ base: 4, md: 5 }}>
                  {!requiresPasswordGate ? (
                    <Text fontSize="xl" lineHeight="1" fontWeight="semibold" textAlign="center" color="fg.inverted">
                      Call Center Donation
                    </Text>
                  ) : null}

                  {!requiresPasswordGate ? (
                    <Button
                      size="md"
                      w="fit-content"
                      alignSelf="center"
                      variant="outline"
                      colorPalette="gray"
                      color="fg.inverted"
                      _hover={{ color: 'fg' }}
                      onClick={handleStartNewDonation}
                    >
                      Start New Donation
                    </Button>
                  ) : null}

                  <Box
                    bg="bg"
                    borderRadius="l3"
                    boxShadow={{ base: 'none', md: 'lg' }}
                    py={{ base: 4, lg: 4 }}
                    pb={{ base: 4, lg: 7 }}
                  >
                    {requiresPasswordGate ? (
                      <Box px={{ base: 6, md: 8 }} pt={{ base: 6, md: 8 }} pb={{ base: 6, md: 8 }}>
                        <Box asChild>
                          <form
                            onSubmit={(event) => {
                              event.preventDefault();
                              submitCallCenterPassword();
                            }}
                            noValidate
                          >
                            <VStack gap={{ base: 5, md: 6 }} align="stretch">
                              <VStack gap={{ base: 2, md: 3 }} align="center">
                                <Heading fontSize="2xl" fontWeight="medium" textAlign="center" lineHeight="32px">
                                  Approved Access Only
                                </Heading>
                                <Text fontSize="md" textAlign="center" color="fg.muted">
                                  This donation form is for internal use only. Enter the password to continue.
                                </Text>
                              </VStack>

                              <VStack gap={{ base: 4, md: 5 }} align="stretch">
                                <Field.Root invalid={Boolean(passwordError)}>
                                  <Field.Label>Password</Field.Label>
                                  <Input
                                    autoFocus
                                    type="password"
                                    value={passwordInput}
                                    onChange={(e) => {
                                      setPasswordInput(e.currentTarget.value);
                                      if (passwordError) setPasswordError(null);
                                    }}
                                    autoComplete="current-password"
                                  />
                                  {passwordError ? <Field.ErrorText>{passwordError}</Field.ErrorText> : null}
                                </Field.Root>

                                <Button type="submit" size="lg" w="full" variant="solid" colorPalette="gray">
                                  Continue
                                </Button>
                              </VStack>
                            </VStack>
                          </form>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {allowCampaignEntry ? (
                          <Box px={{ base: 6, md: 8 }} pt={{ base: 6, md: 8 }}>
                            <Field.Root>
                              <Field.Label>Campaign</Field.Label>
                              <Combobox.Root
                                collection={campaignCollection.collection}
                                openOnClick
                                value={selectedCampaignCode ? [selectedCampaignCode] : []}
                                inputValue={campaignDisplayValue}
                                onValueChange={(e) => {
                                  const next = e.value[0] ?? '';
                                  setSelectedCampaignCode(next);
                                  setCampaignQuery('');
                                }}
                                onInputValueChange={(e) => {
                                  if (selectedCampaignCode && e.inputValue === selectedCampaignCode) {
                                    setCampaignQuery('');
                                    return;
                                  }
                                  setCampaignQuery(e.inputValue);
                                }}
                              >
                                <Combobox.Control position="relative">
                                  <Combobox.Input
                                    h="40px"
                                    placeholder="Campaign code"
                                    pr="10"
                                    color={isCampaignPlaceholder ? 'fg.subtle' : 'fg'}
                                    _placeholder={{ color: 'fg.subtle' }}
                                    overflow="hidden"
                                    whiteSpace="nowrap"
                                    textOverflow="ellipsis"
                                  />
                                  <Combobox.IndicatorGroup position="absolute" right="0" top="50%" transform="translateY(-50%)">
                                    <Combobox.ClearTrigger />
                                    <Combobox.Trigger />
                                  </Combobox.IndicatorGroup>
                                </Combobox.Control>
                                <Combobox.Positioner>
                                  <Combobox.Content>
                                    <Combobox.Empty>
                                      {campaignQuery.trim().length < 2
                                        ? 'Type at least 2 characters'
                                        : isCampaignLoading
                                          ? 'Loading…'
                                          : 'No results'}
                                    </Combobox.Empty>
                                    {campaignItems.map((item) => (
                                      <Combobox.Item key={item.value} item={item}>
                                        <Combobox.ItemText>{item.label}</Combobox.ItemText>
                                        <Combobox.ItemIndicator />
                                      </Combobox.Item>
                                    ))}
                                  </Combobox.Content>
                                </Combobox.Positioner>
                              </Combobox.Root>
                            </Field.Root>
                          </Box>
                        ) : null}

                        {allowCampaignEntry ? (
                          <Box
                            borderTop="1px"
                            borderColor="border"
                            borderStyle="dotted"
                            my={{ base: 4, md: 5 }}
                            mx={{ base: 6, md: 8 }}
                          />
                        ) : null}

                        {formSlug ? (
                          <DonationForm
                            key={`donation-form-${formResetKey}`}
                            formSlug={formSlug}
                            utmCampaign={utmCampaignOverride}
                            sfmcSubkey={normalizedSfmcSubkey}
                            placement={placement}
                            contentOverrides={{ headline: 'Make a Donation', description: null }}
                            tierOverrides={tierOverrides}
                            preview={isPreview}
                            showStepNavigator={false}
                          />
                        ) : (
                          <DonationForm
                            key={`donation-form-${formResetKey}`}
                            useDefault={true}
                            utmCampaign={utmCampaignOverride}
                            sfmcSubkey={normalizedSfmcSubkey}
                            placement={placement}
                            contentOverrides={{ headline: 'Make a Donation', description: null }}
                            tierOverrides={tierOverrides}
                            preview={isPreview}
                            showStepNavigator={false}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </VStack>
              ) : !requiresPasswordGate ? (
                formSlug ? (
                  <DonationForm
                    key={`donation-form-${formResetKey}`}
                    formSlug={formSlug}
                    utmCampaign={utmCampaignOverride}
                    sfmcSubkey={normalizedSfmcSubkey}
                    placement={placement}
                    contentOverrides={{ headline: 'Make a Donation', description: null }}
                    tierOverrides={tierOverrides}
                    preview={isPreview}
                    memberId={prefillMemberId}
                    prefillDonor={prefillDonor}
                    initialSelection={prefillSelection}
                  />
                ) : (
                  <DonationForm
                    key={`donation-form-${formResetKey}`}
                    useDefault={true}
                    utmCampaign={utmCampaignOverride}
                    sfmcSubkey={normalizedSfmcSubkey}
                    placement={placement}
                    contentOverrides={{ headline: 'Make a Donation', description: null }}
                    tierOverrides={tierOverrides}
                    preview={isPreview}
                    memberId={prefillMemberId}
                    prefillDonor={prefillDonor}
                    initialSelection={prefillSelection}
                  />
                )
              ) : null}
            </Box>
          </Stack>
        </Container>
      </Box>

      <Toaster toaster={toaster}>
        {(toast) => (
          <Toast.Root>
            <Toast.Title>{toast.title}</Toast.Title>
            {toast.description ? <Toast.Description>{toast.description}</Toast.Description> : null}
            <Toast.CloseTrigger />
          </Toast.Root>
        )}
      </Toaster>
    </Box>
    </>
  );
};
